import React, { Fragment, useContext, useState } from 'react'
import {
  Button,
  ButtonIcon,
  FontIcon,
  Select,
  tokens,
  Tag,
  Modal
} from '@arvoreeducacao/bonsai-ds'
import type { EntityReaderPayload, User } from 'api/data.types'
import { useUsersTable } from './UsersTable.hooks'
import { colors } from 'config/ui'
import { UserActionsModal, MoveUserModal } from 'components/admin/modals'
import { ModalTypes } from 'components/admin/modals/constants'
import {
  EntityUsersContext,
  EntityUsersDispatchContext
} from 'screens/admin/entity-users-management/EntityUsersManagementProvider'
import {
  Wrapper,
  TopBar,
  UserTableTopBarActionsWrapper,
  TitleLabel,
  StudentName,
  StyledTable,
  TitleActives,
  Title,
  ModalMoreOptionsHeader,
  ListItemContainer,
  NameColumnMobileViewWrapper
} from '../EntityUsersManagement.styles'
import ReactTooltip from 'react-tooltip'
import { convertDateToBR } from 'utils/dates'
import { PERMISSIONS } from 'config/vars'
import EmptySpace from 'components/admin/empty-space'
import { useApolloClient } from '@apollo/client'
import { useTranslation } from 'i18n'
import useUserPlatform from 'utils/hooks/use-platform'
import Avatar from 'components/lib/avatar'
import { ListItemPointer } from 'components/modular/book/book-modal/BookModal.styles'
import usePermissions from 'utils/hooks/use-permissions'

export const USERS_TABLE_WRAPPER_ID = 'users-table-wrapper'

const UsersTable: React.FC<{ user: User }> = ({ user }) => {
  const { t } = useTranslation()
  const client = useApolloClient()
  const {
    usersFromCurrentEntity,
    currentEntity,
    requestedUser,
    readersPaginationCount
  } = useContext(EntityUsersContext)
  const { isSmallerThanTablet } = useUserPlatform()
  const [userForMoreOptions, setUserForMoreOptions] =
    useState<EntityReaderPayload>(null)
  const { canManageUsers, canDeleteUsers } = usePermissions()
  const { loadNextReadersPage, loadPrevReadersPage } = useContext(
    EntityUsersDispatchContext
  )

  const {
    showMoveUserModal,
    showUserModal,
    userModalType,
    userInfo,
    loansActiveOptions,
    handleResetUserPassword,
    handleShowUserCardID,
    handleOpenUserModal,
    handleCloseUserModal,
    handleOpenMoveUserModal,
    handleCloseMoveUserModal,
    handleClickOnStudentCardButton,
    handleOnChangeLoansOptions,
    handleClickMakeLoan
  } = useUsersTable({ client, user })

  const { root, entity } = user.reader

  const {
    currentSimultaneousUsers,
    maxSimultaneousUsers,
    nextReaderSpotAvailable
  } = root
  const isLibraryProject = Boolean(maxSimultaneousUsers)

  const loansCellRenderer = data => {
    const canEnableMoreSimultaneousUsers =
      currentSimultaneousUsers < maxSimultaneousUsers

    // tablets have free access in library project
    if (data.permissions && data.permissions.includes('free_pass')) {
      return <span>{t('Usuário fixo')}</span>
    }

    if (data.accessExpireDate) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              backgroundColor: '#58D68D',
              textAlign: 'center',
              marginRight: '8px'
            }}
          >
            <FontIcon iconName="check" color={'white'} />
          </div>
          {t('Terminar em:')}{' '}
          <span style={{ color: colors.gray9, paddingLeft: '3px' }}>
            {convertDateToBR(data.accessExpireDate)}
          </span>
        </div>
      )
    }

    return (
      <div
        style={{ display: 'inline-block' }}
        data-tip={t('Limite de empréstimos simultâneos atingido')}
        data-for={!canEnableMoreSimultaneousUsers && 'loans-alert'}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              width: '120px',
              marginRight: tokens.spacing.horizontalXsmall
            }}
          >
            <Select
              items={[
                { id: 10, text: '10 dias' },
                { id: 15, text: '15 dias' },
                { id: 20, text: '20 dias' },
                { id: 25, text: '25 dias' },
                { id: 30, text: '30 dias' }
              ]}
              defaultOption={
                loansActiveOptions.find(l => l.readerId === data.id)?.option
              }
              placeholder={t('Tempo')}
              disabled={!canEnableMoreSimultaneousUsers}
              onChange={item => handleOnChangeLoansOptions(item, data.id)}
            />
          </div>

          <Button
            theme={{
              type: 'primaryLivros',
              size: 'medium'
            }}
            text={
              loansActiveOptions.some(
                loan => loan.readerId === data.id && loan.isMakingLoan
              )
                ? t('Emprestando...')
                : t('Emprestar')
            }
            disabled={
              !canEnableMoreSimultaneousUsers ||
              (canEnableMoreSimultaneousUsers &&
                !loansActiveOptions.find(l => l.readerId === data.id)) ||
              loansActiveOptions.some(
                loan => loan.readerId === data.id && loan.isMakingLoan
              )
            }
            onClick={() => handleClickMakeLoan(data)}
          />
        </div>
      </div>
    )
  }

  const columns = [
    {
      Header: t('NOME'),
      accessor: 'user',
      Cell: function data({ row }) {
        return (
          <Fragment>
            <StudentName>{row.original.name}</StudentName>
            {Boolean(row.original.teacherId) && (
              <Tag type="primaryDark">{t('colaborador')}</Tag>
            )}
          </Fragment>
        )
      },
      style: {
        width: '100%'
      }
    },
    ...[
      isLibraryProject
        ? {
            accessor: 'token-volante',
            Header: t('Empréstimo'),
            style: { height: '80px' },
            disableSortBy: true,
            Cell: ({ row }) => loansCellRenderer(row.original)
          }
        : null
    ],
    {
      Header: '',
      accessor: 'actions',
      disableSortBy: true,
      Cell: function Actions({ row }) {
        const editedUserHasAdminPermissions =
          row.original && row.original.teacherPermissions
            ? row.original.teacherPermissions.includes(
                PERMISSIONS.CAN_CREATE_READER ||
                  PERMISSIONS.CAN_CREATE_ADMIN ||
                  PERMISSIONS.CAN_DELETE_READER ||
                  PERMISSIONS.CAN_DELETE_ADMIN
              )
            : false

        const canSeeUserToken = Boolean(
          canManageUsers ||
            !row.original.teacherPermissions ||
            !editedUserHasAdminPermissions
        )

        const rowObject = {
          ...row.original,
          showUserSensitiveInfo: canSeeUserToken
        }

        const newPermissionsManagement =
          user.featureToggles?.newPermissionsManagement

        return (
          <div
            style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'end'
            }}
          >
            <ReactTooltip place="top" effect="solid" />
            <Button
              data-testid={`student-card-${rowObject.id}`}
              iconName="download"
              disabled={!canSeeUserToken}
              isLeft
              theme={{ type: 'ghost', size: 'medium' }}
              text={t('Baixar carteirinha')}
              onClick={() => handleShowUserCardID(rowObject)}
            />
            {(!newPermissionsManagement || canManageUsers) && (
              <Button
                data-testid={`reset-reader-${rowObject.id}-password`}
                theme={{ type: 'ghost', size: 'medium' }}
                text={t('Resetar senha')}
                onClick={() => handleResetUserPassword(rowObject)}
              />
            )}
            <ButtonIcon
              data-testid={`edit-reader-${rowObject.id}`}
              iconName="edit"
              onClick={() => {
                handleOpenUserModal(ModalTypes.edit, rowObject)
              }}
              theme={{ type: 'ghost', size: 'medium' }}
              data-tip={t('Editar usuário')}
            />
            <ButtonIcon
              data-testid={`move-reader-${rowObject.id}`}
              iconName="folder-upload"
              onClick={() => handleOpenMoveUserModal(rowObject)}
              theme={{ type: 'ghost', size: 'medium' }}
              data-tip={t('Mover usuário')}
            />
            {canDeleteUsers && (
              <ButtonIcon
                data-testid={`delete-reader-${rowObject.id}`}
                iconName="trash-cancel"
                onClick={() => {
                  handleOpenUserModal(ModalTypes.delete, rowObject)
                }}
                theme={{ type: 'ghost', size: 'medium' }}
                data-tip={t('Excluir usuário')}
              />
            )}
          </div>
        )
      }
    }
  ].filter(c => Boolean(c))

  const mobileColumns = [
    {
      Header: t('NOME'),
      accessor: 'user',
      Cell: function data({ row }) {
        return (
          <NameColumnMobileViewWrapper>
            <StudentName>{row.original.name}</StudentName>
            {Boolean(row.original.teacherId) && (
              <Tag type="primaryDark">{t('colaborador')}</Tag>
            )}
          </NameColumnMobileViewWrapper>
        )
      },
      style: {
        width: '100%'
      }
    },
    {
      Header: '',
      accessor: 'actions',
      disableSortBy: true,
      style: {
        width: 'min-content'
      },
      Cell: function Actions({ row }) {
        return (
          <ButtonIcon
            iconName={'ellipsis-vertical'}
            theme={{
              type: 'ghost',
              size: 'medium'
            }}
            onClick={e => {
              e.stopPropagation()
              setUserForMoreOptions(row.original)
            }}
          />
        )
      }
    }
  ]

  return (
    <Wrapper id={USERS_TABLE_WRAPPER_ID}>
      <ReactTooltip id={`loans-alert`} place="top" effect="float" />
      {showUserModal && (
        <UserActionsModal
          type={userModalType}
          client={client}
          isOpen={showUserModal}
          onClose={handleCloseUserModal}
          entityId={currentEntity.id}
          userTeacherPermissions={userInfo ? userInfo.teacherPermissions : null}
          userId={userInfo ? userInfo.userId : null}
          userName={userInfo ? userInfo.name : null}
          userEmail={userInfo ? userInfo.email : null}
          readerId={userInfo ? userInfo.id : null}
          userIsAdmin={userInfo ? Boolean(userInfo.teacherId) : null}
          userAccessToken={userInfo ? userInfo.accessToken : null}
          editorCanSeeUserToken={
            userInfo ? userInfo.showUserSensitiveInfo : null
          }
        />
      )}
      {showMoveUserModal && (
        <MoveUserModal
          currentEntity={entity}
          reader={userInfo ? userInfo : null}
          client={client}
          isOpen={showMoveUserModal}
          onClose={handleCloseMoveUserModal}
        />
      )}
      {userForMoreOptions && (
        <Modal
          isOpen
          type="standard"
          size="small"
          title={t('Mais opções')}
          titleSize={4}
          hasDesktopCloseButton
          onClose={() => setUserForMoreOptions(null)}
          center
          sheetHeaderProps={{
            type: 'close',
            title: t('Mais opções'),
            onClose: () => setUserForMoreOptions(null)
          }}
        >
          <ModalMoreOptionsHeader>
            <Avatar name={user?.name} size={'big'} />
            <h4>{user?.name}</h4>
          </ModalMoreOptionsHeader>

          <ListItemContainer>
            <ListItemPointer
              icon="download"
              text={t('Baixar carteirinha')}
              onClick={() => handleShowUserCardID(userForMoreOptions)}
            />

            <ListItemPointer
              icon="padlock"
              text={t('Resetar senha')}
              onClick={() => handleResetUserPassword(userForMoreOptions)}
            />

            <ListItemPointer
              icon="edit"
              text={t('Editar usuário')}
              onClick={() =>
                handleOpenUserModal(ModalTypes.edit, userForMoreOptions)
              }
            />

            <ListItemPointer
              icon="folder-upload"
              text={t('Mover usuário')}
              onClick={() => handleOpenMoveUserModal(userForMoreOptions)}
            />

            {canDeleteUsers && (
              <ListItemPointer
                icon="trash-cancel"
                text={t('Excluir usuário')}
                onClick={() =>
                  handleOpenUserModal(ModalTypes.delete, userForMoreOptions)
                }
              />
            )}
          </ListItemContainer>
        </Modal>
      )}
      <TopBar>
        <div>
          <Title>
            {t('Usuários')} <TitleLabel>({currentEntity.userCount})</TitleLabel>
          </Title>
          {isLibraryProject && (
            <TitleActives>
              <React.Fragment>
                {t('Empréstimos ativos')}: {currentSimultaneousUsers} {t('de')}{' '}
                {maxSimultaneousUsers}
                {currentSimultaneousUsers === maxSimultaneousUsers && (
                  <span>
                    {t('Próxima liberação dia')} {nextReaderSpotAvailable}
                  </span>
                )}
              </React.Fragment>
            </TitleActives>
          )}
        </div>
        <UserTableTopBarActionsWrapper>
          <Button
            style={{ height: 'fit-content' }}
            disabled={!currentEntity.userCount}
            iconName="download"
            isLeft
            theme={{
              type: 'secondary',
              size: 'large'
            }}
            text={t('Baixar carteirinhas')}
            onClick={() => handleClickOnStudentCardButton(currentEntity)}
          />
          {canManageUsers && (
            <Button
              style={{ height: 'fit-content' }}
              iconName="add"
              isLeft
              theme={{
                type: 'primaryLivros',
                size: 'large'
              }}
              text={t('Adicionar usuário')}
              onClick={() => handleOpenUserModal(ModalTypes.create)}
            />
          )}
        </UserTableTopBarActionsWrapper>
      </TopBar>
      {currentEntity.userCount ? (
        <StyledTable
          data-test="clients-list-table"
          paginationPosition={'bottom'}
          data={[
            ...[requestedUser],
            ...usersFromCurrentEntity.filter(
              u =>
                (requestedUser && requestedUser.id !== u.id) || !requestedUser
            )
          ].filter(u => Boolean(u))}
          disablePageSizeOptions={true}
          columns={isSmallerThanTablet ? mobileColumns : columns}
          manualPagination={true}
          controlledPageSize={20}
          // determine the amount of pages available
          controlledPageCount={Math.ceil(currentEntity.userCount / 20)}
          dataLength={currentEntity.userCount}
          controlledPageIndex={readersPaginationCount - 1}
          controlledPrevPage={() => {
            loadPrevReadersPage()
          }}
          controlledNextPage={() => {
            loadNextReadersPage()
          }}
        />
      ) : (
        <EmptySpace text={t('Nenhum usuário encontrado.')} />
      )}
    </Wrapper>
  )
}

export default UsersTable
